import React, { useEffect, useRef } from 'react';
import { ArrowRight, Play, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Hero.css';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const Hero = () => {
    const { t } = useTranslation();
    const heroRef = useRef(null);
    const titleRef = useRef(null);
    const subtitleRef = useRef(null);
    const buttonsRef = useRef(null);
    const featuresRef = useRef(null);
    const imageRef = useRef(null);

    useEffect(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        // Animate title
        if (titleRef.current) {
            gsap.set(titleRef.current, { opacity: 0, y: 50 });
            tl.to(titleRef.current, { opacity: 1, y: 0, duration: 1 });
        }

        // Animate subtitle
        if (subtitleRef.current) {
            gsap.set(subtitleRef.current, { opacity: 0, y: 30 });
            tl.to(subtitleRef.current, { opacity: 1, y: 0, duration: 0.8 }, '-=0.5');
        }

        // Animate buttons
        if (buttonsRef.current) {
            gsap.set(buttonsRef.current.children, { opacity: 0, scale: 0.8 });
            tl.to(
                buttonsRef.current.children,
                { opacity: 1, scale: 1, duration: 0.6, stagger: 0.2 },
                '-=0.4'
            );
        }

        // Animate features
        if (featuresRef.current) {
            gsap.set(featuresRef.current.children, { opacity: 0, x: -30 });
            tl.to(
                featuresRef.current.children,
                { opacity: 1, x: 0, duration: 0.6, stagger: 0.15 },
                '-=0.6'
            );
        }

        // Animate image
        if (imageRef.current) {
            gsap.set(imageRef.current, { opacity: 0, scale: 1.1, x: 50 });
            tl.to(imageRef.current, { opacity: 1, scale: 1, x: 0, duration: 1.2 }, '-=1');

            // Parallax effect
            ScrollTrigger.create({
                trigger: heroRef.current,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
                onUpdate: (self) => {
                    gsap.to(imageRef.current, {
                        y: self.progress * 100,
                        opacity: 1 - self.progress * 0.3,
                        ease: 'none',
                    });
                },
            });

            // Floating animation
            gsap.to(imageRef.current, {
                y: -20,
                duration: 3,
                ease: 'sine.inOut',
                repeat: -1,
                yoyo: true,
            });
        }

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => {
                if (trigger.vars.trigger === heroRef.current) {
                    trigger.kill();
                }
            });
        };
    }, []);

    const scrollToContact = () => {
        const element = document.getElementById('contact');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section id="home" ref={heroRef} className="hero">
            <div className="hero-background">
                <div className="hero-gradient"></div>
                <div className="hero-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                    <div className="shape shape-3"></div>
                </div>
            </div>
            <div className="hero-container">
                <div className="hero-content">
                    <div className="hero-badge">
                        <span>{t('hero.badge')}</span>
                    </div>
                    <h1 ref={titleRef} className="hero-title">
                        {t('hero.title')}
                        <span className="gradient-text"> {t('hero.titleHighlight')}</span>
                    </h1>
                    <p ref={subtitleRef} className="hero-subtitle">
                        {t('hero.subtitle')}
                    </p>
                    <div ref={buttonsRef} className="hero-actions">
                        <button className="btn-primary btn-large" onClick={scrollToContact}>
                            {t('hero.bookAppointment')}
                            <ArrowRight size={20} />
                        </button>
                        <button className="btn-secondary btn-large">
                            <Play size={20} />
                            {t('hero.watchVideo')}
                        </button>
                    </div>
                    <div ref={featuresRef} className="hero-features">
                        <div className="feature-item">
                            <CheckCircle size={20} className="feature-icon" />
                            <span>{t('hero.features.emergency')}</span>
                        </div>
                        <div className="feature-item">
                            <CheckCircle size={20} className="feature-icon" />
                            <span>{t('hero.features.expert')}</span>
                        </div>
                        <div className="feature-item">
                            <CheckCircle size={20} className="feature-icon" />
                            <span>{t('hero.features.technology')}</span>
                        </div>
                    </div>
                </div>
                <div className="hero-image">
                    <div ref={imageRef} className="hero-image-wrapper">
                        <div className="image-placeholder">
                            <img 
                                src={`${process.env.PUBLIC_URL || ''}/hero.png`} 
                                alt="Dr. Sachin Kumar providing exceptional dental care" 
                                className="hero-team-image"
                            />
                        </div>
                        <div className="floating-card card-1">
                            <div className="card-icon">❤️</div>
                            <div className="card-content">
                                <span className="card-value">100%</span>
                                <span className="card-label">{t('hero.stats.satisfaction')}</span>
                            </div>
                        </div>
                        <div className="floating-card card-2">
                            <div className="card-icon">🏆</div>
                            <div className="card-content">
                                <span className="card-value">15+</span>
                                <span className="card-label">{t('hero.stats.doctors')}</span>
                            </div>
                        </div>
                        <div className="floating-card card-3">
                            <div className="card-icon">🦷</div>
                            <div className="card-content">
                                <span className="card-value">15+</span>
                                <span className="card-label">{t('hero.stats.experience')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="hero-scroll">
                <div className="scroll-indicator">
                    <div className="scroll-line"></div>
                </div>
                <span>{t('hero.scrollDown')}</span>
            </div>
        </section>
    );
};

export default Hero;

